import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import BrandLogo from '../components/BrandLogo';
import { getStoredCoupon, getStoredReferral, clearStoredReferral } from '../utils/couponReferral';

// FastAPI validation errors (422) return `detail` as an array of Pydantic error
// objects, not a string — guard against rendering that raw shape in a toast.
const extractErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
  return 'No se pudo iniciar el pago. Intenta de nuevo.';
};

const TIER_RANK = { entrepreneur: 0, pro: 1, elite: 2 };

const TIER_LABELS = { entrepreneur: 'Emprendedor', pro: 'Pro', elite: 'Elite' };

const PLANS = [
  {
    tier: 'entrepreneur',
    name: 'Emprendedor',
    price: 29,
    description: 'Para vendedores que tienen hasta 100 ordenes por mes.',
    features: [
      'Dashboard de rentabilidad, ultimos 3 meses de ordenes procesadas y en adelante',
      '7 Generaciones de imágenes',
      '50 Busquedas de producto',
      'Soporte por correo',
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    price: 69,
    description: 'Para operaciones en crecimiento que tienen hasta 500 ordenes por mes.',
    features: [
      'Todo lo del plan Emprendedor',
      '15 Generaciones de imágenes',
      '100 Busquedas de producto',
      'Soporte prioritario',
    ],
    highlighted: true,
  },
  {
    tier: 'elite',
    name: 'Elite',
    price: 129,
    description: 'Para cuentas de alta operacion y productividad. Hasta 1000 ordenes por mes.',
    features: [
      'Todo lo del plan Pro',
      '30 Generaciones de imágenes',
      '175 Busquedas de producto',
      'Soporte dedicado',
    ],
  },
];

const SelectPlan = () => {
  const navigate = useNavigate();
  const { user, logout, isConsultant, hasActiveSubscription, subscriptionStatus, tierUpgrade, affiliateDiscount, refreshSubscriptionStatus } = useAuth();
  const [loadingTier, setLoadingTier] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  // Pre-filled from a QR-code/campaign link if one was scanned earlier in
  // this session (see utils/couponReferral.js) — still editable by hand.
  const [couponCode, setCouponCode] = useState(() => getStoredCoupon());
  // A consultant's ?ref= link not yet attached to this account (e.g. signed
  // up through MercadoLibre instead of /register) — checkout attaches it.
  const [pendingReferral, setPendingReferral] = useState(null);
  const referralDiscount = affiliateDiscount
    ? { program: affiliateDiscount.program, percentOff: affiliateDiscount.percent_off, months: affiliateDiscount.months_remaining }
    : pendingReferral;

  useEffect(() => {
    const code = getStoredReferral();
    if (!code || affiliateDiscount) return;
    apiClient.get(`/api/affiliates/validate/${encodeURIComponent(code)}`)
      .then((res) => {
        if (res.data.valid) {
          setPendingReferral({
            program: res.data.program,
            percentOff: res.data.percent_off,
            months: res.data.months,
            referrerName: res.data.referrer_name,
          });
        }
      })
      .catch(() => {});
  }, [affiliateDiscount]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (isConsultant) {
      navigate('/afiliados', { replace: true });
      return;
    }
    if (subscriptionStatus !== null && hasActiveSubscription) {
      navigate('/connect-meli', { replace: true });
    }
  }, [user, isConsultant, hasActiveSubscription, subscriptionStatus, navigate]);

  const handleSelectPlan = async (tier) => {
    setLoadingTier(tier);
    try {
      // An affiliate discount can't be combined with a coupon (backend enforces it too).
      const trimmedCoupon = referralDiscount ? '' : couponCode.trim();
      const referralCode = getStoredReferral();
      const response = await apiClient.post('/api/subscriptions/checkout-session', {
        tier,
        ...(trimmedCoupon ? { coupon: trimmedCoupon } : {}),
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      clearStoredReferral();
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      // Existing paying subscription changed tier in place (Stripe prorated
      // the difference) — no checkout redirect, just refresh and move on.
      toast.success(`Plan actualizado a ${TIER_LABELS[tier] || tier}. Se cobró la diferencia prorrateada.`);
      await refreshSubscriptionStatus();
      navigate('/connect-meli', { replace: true });
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setLoadingTier(null);
    }
  };

  if (!user || isConsultant || subscriptionStatus === null || hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <BrandLogo className="h-8 mx-auto mb-6" />
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Elige tu plan
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Necesitas una suscripción activa para conectar tu cuenta de Mercado Libre y empezar a usar la plataforma.
          </p>
        </div>

        {tierUpgrade && (
          <div className="max-w-2xl mx-auto mb-8 rounded-lg border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-center">
            <p className="text-sm text-foreground">
              Tuviste <strong>{tierUpgrade.ordersLastMonth}</strong> pedidos facturados el mes pasado, lo que supera
              el límite del plan <strong>{TIER_LABELS[tierUpgrade.currentTier] || tierUpgrade.currentTier}</strong>.
              Para seguir usando la plataforma necesitas actualizar al plan{' '}
              <strong>{TIER_LABELS[tierUpgrade.requiredTier] || tierUpgrade.requiredTier}</strong> o superior.
            </p>
            {tierUpgrade.discountPercentOff && (
              <p className="text-sm text-foreground mt-2">
                Tu descuento del <strong>{tierUpgrade.discountPercentOff}%</strong> se mantiene y se aplicará
                automáticamente al nuevo plan
                {tierUpgrade.discountEndsAt
                  ? ` hasta el ${new Date(tierUpgrade.discountEndsAt).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}.`
                  : '.'}
              </p>
            )}
          </div>
        )}

        {referralDiscount ? (
          <div className="max-w-2xl mx-auto mb-8 rounded-lg border border-green-500/40 bg-green-500/10 px-5 py-4 text-center">
            <p className="text-sm text-foreground">
              {referralDiscount.program === 'friend' ? (
                <>
                  {referralDiscount.referrerName ? <strong>{referralDiscount.referrerName}</strong> : 'Un amigo'} te invitó a Selltrix:
                  tienes un <strong>{referralDiscount.percentOff}% de descuento</strong> en tu primer mes. Se aplica automáticamente al pagar.
                </>
              ) : (
                <>
                  Llegaste por el enlace de un consultor Selltrix: tienes un <strong>{referralDiscount.percentOff}% de descuento</strong>{' '}
                  durante {referralDiscount.months} {referralDiscount.months === 1 ? 'mes' : 'meses'}. Se aplica automáticamente al pagar.
                </>
              )}
            </p>
          </div>
        ) : (
          <div className="max-w-xs mx-auto mb-8">
            <Label htmlFor="coupon-code" className="text-sm text-muted-foreground">
              Código de cupón (opcional)
            </Label>
            <Input
              id="coupon-code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Ej: BIENVENIDA20"
              className="mt-1.5"
              disabled={loadingTier !== null}
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const isSelected = selectedTier === plan.tier;
            const isBelowRequired = tierUpgrade && TIER_RANK[plan.tier] < TIER_RANK[tierUpgrade.requiredTier];
            return (
            <Card
              key={plan.tier}
              onClick={() => !isBelowRequired && setSelectedTier(plan.tier)}
              className={`transition-all backdrop-blur-xl flex flex-col ${
                isBelowRequired
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer ' + (isSelected ? 'border-blue-500 shadow-lg shadow-blue-500/20 md:-translate-y-2' : 'hover:border-primary/40')
              }`}
            >
              <CardHeader>
                {plan.highlighted && (
                  <span className="inline-block w-fit text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1 mb-2">
                    Más popular
                  </span>
                )}
                <CardTitle className="text-foreground text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="pt-2">
                  <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                  <span className="text-muted-foreground"> USD / mes</span>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTier(plan.tier);
                    handleSelectPlan(plan.tier);
                  }}
                  disabled={loadingTier !== null || isBelowRequired}
                  className={`w-full h-11 ${
                    isSelected
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-muted hover:bg-accent text-foreground'
                  }`}
                >
                  {loadingTier === plan.tier ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirigiendo...
                    </>
                  ) : isBelowRequired ? (
                    'No disponible por tu volumen'
                  ) : (
                    `Elegir ${plan.name}`
                  )}
                </Button>
              </CardFooter>
            </Card>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={logout}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectPlan;
